import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ArrowLeft, Send, Sparkles, ConciergeBell as ConciergeIcon, Video, Calendar, MapPin, PhoneCall, Star, Download } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` } });
const STATUS_BADGE = {
  new: 'bg-amber-100 text-amber-700', assigned: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-indigo-100 text-indigo-700', completed: 'bg-green-100 text-green-700', cancelled: 'bg-gray-200 text-gray-600',
};
const STATUS_LABEL = { new: 'Received', assigned: 'Matchmaker assigned', in_progress: 'In progress', completed: 'Completed', cancelled: 'Cancelled' };

const ConciergePage = ({ user }) => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [dates, setDates] = useState([]);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [r, m, pr, dt] = await Promise.all([
        axios.get(`${API}/services/mine`, authHeaders()),
        axios.get(`${API}/concierge/messages`, authHeaders()),
        axios.get(`${API}/concierge/proposals`, authHeaders()),
        axios.get(`${API}/dates/mine`, authHeaders()),
      ]);
      setRequests(r.data);
      setMessages(m.data);
      setProposals(pr.data);
      setDates(dt.data);
    } catch (e) { toast.error('Failed to load your concierge'); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const respond = async (id, response) => {
    let decline_reason = null;
    if (response === 'decline') {
      decline_reason = window.prompt('Passing on this introduction. Optionally tell your matchmaker why (helps improve future matches):', '');
      if (decline_reason === null) return; // cancelled
    }
    try {
      const res = await axios.post(`${API}/concierge/proposals/${id}/respond`, { response, decline_reason }, authHeaders());
      toast.success(res.data.message);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const dateAction = async (id, action) => {
    try {
      await axios.post(`${API}/dates/${id}/${action}`, {}, authHeaders());
      toast.success(action === 'confirm' ? 'Date confirmed' : 'Date cancelled');
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const submitFeedback = async (id, payload) => {
    try {
      await axios.post(`${API}/dates/${id}/feedback`, payload, authHeaders());
      toast.success('Thanks for your feedback!');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit'); }
  };

  const downloadResult = async (fileId, filename) => {
    try {
      const res = await axios.get(`${API}/files/${fileId}/download`, { ...authHeaders(), responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = filename || 'result'; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error('Could not download file'); }
  };

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/concierge/messages`, { body: body.trim() }, authHeaders());
      setMessages((prev) => [...prev, res.data]);
      setBody('');
    } catch (e) { toast.error('Failed to send'); }
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <Button data-testid="back-btn" variant="ghost" onClick={() => navigate('/dashboard')} className="mb-6 text-rose-700">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
        </Button>

        <div className="flex items-center gap-3 mb-2">
          <ConciergeIcon className="w-9 h-9 text-rose-600" />
          <h1 className="text-5xl font-bold text-rose-900" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Your Concierge</h1>
        </div>
        <p className="text-gray-500 mb-8">Your dedicated line to our matchmaking team for premium services.</p>

        {proposals.length > 0 && (
          <Card className="shadow-xl border-rose-100 mb-8" data-testid="proposals-card">
            <CardHeader><CardTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-amber-500" /> Curated Introductions</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg bg-rose-50 border border-rose-100 p-3 text-xs text-gray-600" data-testid="what-happens-next">
                <span className="font-semibold text-rose-800">What happens next:</span> Review the introduction → Both of you accept → Your private chat opens → Schedule a date → Share feedback. Introductions expire after 72 hours, so don't keep them waiting!
              </div>
              {proposals.map((p) => {
                const deadline = p.expires_at && p.status === 'proposed' ? new Date(p.expires_at) : null;
                return (
                <div key={p.id} className="border border-rose-100 rounded-xl p-4" data-testid={`proposal-${p.id}`}>
                  <div className="flex items-center gap-3">
                    {p.other?.picture ? <img src={p.other.picture} alt="" className="w-14 h-14 rounded-full object-cover" /> : <div className="w-14 h-14 rounded-full bg-rose-100" />}
                    <div className="flex-1">
                      <p className="font-semibold text-rose-900">{p.other?.name}{p.other?.age ? `, ${p.other.age}` : ''}</p>
                      {(p.other?.city || p.other?.country) && <p className="text-xs text-gray-500">📍 {[p.other?.city, p.other?.country].filter(Boolean).join(', ')}</p>}
                    </div>
                    {p.status === 'mutual'
                      ? <Badge className="bg-green-100 text-green-700">It's a match! 🎉</Badge>
                      : p.status === 'expired'
                        ? <Badge className="bg-gray-200 text-gray-500">Expired</Badge>
                        : p.my_response === 'accept'
                          ? <Badge className="bg-blue-100 text-blue-700">Waiting on them…</Badge>
                          : <Badge className="bg-amber-100 text-amber-700">New introduction</Badge>}
                  </div>
                  {p.other?.bio && <p className="text-sm text-gray-600 mt-2">{p.other.bio}</p>}
                  {p.reason && <p className="text-sm text-rose-700 mt-2 italic">"{p.reason}" — your matchmaker</p>}
                  {deadline && p.my_response !== 'accept' && (
                    <p className="text-xs text-amber-600 mt-2" data-testid={`deadline-${p.id}`}>⏳ Respond by {deadline.toLocaleDateString()} {deadline.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                  )}
                  {p.status === 'mutual' ? (
                    <Button data-testid={`open-chat-${p.id}`} className="mt-3 bg-gradient-to-r from-amber-500 to-rose-500 text-white" onClick={() => navigate(`/messages/${p.match_id}`)}>
                      Open Chat
                    </Button>
                  ) : p.status !== 'expired' && p.my_response !== 'accept' && (
                    <div className="flex gap-2 mt-3">
                      <Button data-testid={`accept-${p.id}`} size="sm" className="bg-rose-600 hover:bg-rose-700" onClick={() => respond(p.id, 'accept')}>Accept Introduction</Button>
                      <Button data-testid={`decline-${p.id}`} size="sm" variant="outline" onClick={() => respond(p.id, 'decline')}>Pass</Button>
                    </div>
                  )}
                </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {dates.length > 0 && (
          <Card className="shadow-xl border-rose-100 mb-8" data-testid="dates-card">
            <CardHeader><CardTitle className="flex items-center gap-2"><Calendar className="w-5 h-5 text-rose-500" /> Your Dates</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {dates.map((d) => {
                const isParticipant = (d.participants || []).includes(user.id);
                const iConfirmed = (d.confirmations || {})[user.id];
                const fmt = d.format || d.mode;
                const ModeIcon = fmt === 'video' ? Video : fmt === 'audio' || fmt === 'call' ? PhoneCall : MapPin;
                const fmtLabel = fmt === 'video' ? 'Video' : (fmt === 'audio' || fmt === 'call') ? 'Audio' : 'In-person';
                const typeLabel = d.meeting_type === 'interview' ? 'Interview' : d.meeting_type === 'chaperoned' ? 'Chaperoned intro' : `${fmtLabel} date`;
                const withNames = (d.others && d.others.length > 0) ? d.others.map((o) => o.name).join(' & ') : d.other?.name;
                const canJoin = (fmt === 'video' || fmt === 'audio');
                return (
                  <div key={d.id} className="border border-rose-100 rounded-xl p-4" data-testid={`date-${d.id}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-3">
                        {d.other?.picture ? <img src={d.other.picture} alt="" className="w-12 h-12 rounded-full object-cover" /> : <div className="w-12 h-12 rounded-full bg-rose-100" />}
                        <div>
                          <p className="font-semibold text-rose-900 flex items-center gap-1.5"><ModeIcon className="w-4 h-4 text-rose-500" /> {typeLabel}{withNames ? ` with ${withNames}` : ''}</p>
                          <p className="text-xs text-gray-500">{new Date(d.scheduled_at).toLocaleString()} · {d.duration_min} min{d.location ? ` · ${d.location}` : ''}{d.is_host ? ' · You host' : d.host ? ` · Hosted by ${d.host.name}` : ''}</p>
                        </div>
                      </div>
                      <Badge className={d.status === 'confirmed' ? 'bg-green-100 text-green-700' : d.status === 'cancelled' ? 'bg-gray-200 text-gray-600' : d.status === 'completed' ? 'bg-indigo-100 text-indigo-700' : 'bg-amber-100 text-amber-700'}>{d.status.charAt(0).toUpperCase() + d.status.slice(1)}</Badge>
                    </div>
                    {d.notes && <p className="text-sm text-gray-600 mt-2 italic">"{d.notes}"</p>}
                    {d.status !== 'cancelled' && d.status !== 'completed' && (
                      <div className="flex gap-2 mt-3 flex-wrap">
                        {isParticipant && !iConfirmed && <Button data-testid={`confirm-date-${d.id}`} size="sm" className="bg-rose-600 hover:bg-rose-700" onClick={() => dateAction(d.id, 'confirm')}>Confirm</Button>}
                        {canJoin && d.joinable && <Button data-testid={`join-video-${d.id}`} size="sm" className="bg-gradient-to-r from-amber-500 to-rose-500 text-white" onClick={() => navigate(`/video/${d.id}`)}><Video className="w-4 h-4 mr-1" /> Join {fmt === 'audio' ? 'Audio' : 'Video'} Call</Button>}
                        {canJoin && !d.joinable && <span className="text-xs text-gray-400 self-center">Call link opens 10 min before</span>}
                        <Button data-testid={`cancel-date-${d.id}`} size="sm" variant="outline" onClick={() => dateAction(d.id, 'cancel')}>Cancel</Button>
                      </div>
                    )}
                    {isParticipant && d.meeting_type !== 'interview' && (d.status === 'completed' || d.status === 'confirmed') && !d.feedback_given && (
                      <FeedbackDialog dateId={d.id} otherName={d.other?.name} onSubmit={submitFeedback} />
                    )}
                    {isParticipant && d.meeting_type !== 'interview' && d.feedback_given && <p className="text-xs text-green-600 mt-3">✓ Feedback submitted</p>}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {requests.length > 0 && (
          <Card className="shadow-xl border-rose-100 mb-8" data-testid="service-requests-card">
            <CardHeader><CardTitle>Your Services</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {requests.map((r) => (
                <div key={r.id} className="flex items-center justify-between border border-rose-100 rounded-lg p-3 flex-wrap gap-2" data-testid={`service-${r.id}`}>
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-amber-500" />
                    <span className="font-medium text-rose-900">{r.service_label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {r.result_file && (
                      <Button data-testid={`download-result-${r.id}`} size="sm" variant="outline" className="border-rose-200 text-rose-700"
                        onClick={() => downloadResult(r.result_file.id, r.result_file.original_filename)}>
                        <Download className="w-4 h-4 mr-1" /> Download Result
                      </Button>
                    )}
                    <Badge className={STATUS_BADGE[r.status] || ''}>{STATUS_LABEL[r.status] || r.status}</Badge>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <Card className="shadow-xl border-rose-100">
          <CardHeader><CardTitle>Messages</CardTitle></CardHeader>
          <CardContent>
            <div className="h-[380px] overflow-y-auto space-y-3 mb-4 pr-2" data-testid="concierge-thread">
              {messages.length === 0 ? (
                <p className="text-center text-gray-400 py-16">No messages yet. Say hello — our team is here to help.</p>
              ) : messages.map((m) => (
                <div key={m.id} className={`flex ${m.from_staff ? 'justify-start' : 'justify-end'}`}>
                  <div className="max-w-[75%]">
                    <div className={`rounded-2xl px-4 py-2 ${m.from_staff ? (m.style === 'important' ? 'bg-amber-50 border-2 border-amber-300 text-amber-900' : 'bg-rose-100 text-rose-900') : 'bg-gradient-to-r from-amber-400 to-rose-400 text-white'}`}>
                      {m.from_staff && <p className="text-xs font-semibold mb-0.5 text-rose-600">{m.sender_name} · Team</p>}
                      {m.from_staff && m.style === 'important' && <span className="inline-block text-[10px] font-bold uppercase bg-amber-500 text-white px-1.5 py-0.5 rounded mb-1">Important</span>}
                      {m.title && <p className="font-bold text-sm mb-0.5">{m.title}</p>}
                      <p className="text-sm">{m.body}</p>
                    </div>
                    {!m.from_staff && <p className="text-[10px] text-gray-400 mt-0.5 text-right">{m.read_by_staff ? 'Read ✓✓' : 'Sent ✓'}</p>}
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>
            <div className="flex gap-2">
              <Textarea data-testid="concierge-input" value={body} onChange={(e) => setBody(e.target.value)}
                placeholder="Write a message to our team..." className="resize-none" rows={2}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} />
              <Button data-testid="concierge-send-btn" aria-label="Send message" onClick={send} disabled={sending}
                className="bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-600 hover:to-rose-600 text-white self-end h-12 px-5">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ConciergePage;

const FeedbackDialog = ({ dateId, otherName, onSubmit }) => {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [again, setAgain] = useState(null);
  const [comments, setComments] = useState('');

  const submit = () => {
    if (!rating || again === null) { toast.error('Please add a rating and answer'); return; }
    onSubmit(dateId, { rating, would_meet_again: again, comments });
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`feedback-btn-${dateId}`} size="sm" variant="outline" className="mt-3 border-rose-200 text-rose-700">
          <Star className="w-4 h-4 mr-1" /> Leave Feedback
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>How was your date with {otherName}?</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <p className="text-sm font-medium mb-1">Rating</p>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" data-testid={`star-${dateId}-${n}`} onClick={() => setRating(n)}>
                  <Star className={`w-7 h-7 ${n <= rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-sm font-medium mb-1">Would you like to see them again?</p>
            <div className="flex gap-2">
              <Button type="button" size="sm" variant={again === true ? 'default' : 'outline'} className={again === true ? 'bg-rose-600' : ''} data-testid={`again-yes-${dateId}`} onClick={() => setAgain(true)}>Yes</Button>
              <Button type="button" size="sm" variant={again === false ? 'default' : 'outline'} className={again === false ? 'bg-gray-600' : ''} data-testid={`again-no-${dateId}`} onClick={() => setAgain(false)}>No</Button>
            </div>
          </div>
          <Textarea placeholder="Private notes for our matchmaking team (optional)" value={comments} onChange={(e) => setComments(e.target.value)} rows={3} className="resize-none" data-testid={`feedback-comments-${dateId}`} />
          <Button onClick={submit} className="w-full bg-rose-600 hover:bg-rose-700" data-testid={`submit-feedback-${dateId}`}>Submit Feedback</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
